// dependencies
import React, { useEffect, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { useWindowSize } from "usehooks-ts";

import { useNavigation } from "../../../components/Navbar/NavigationContext";
import { SideNavigationLink } from "../../../components/SideNavigation";
// components
import ComponentLoader from "../../../components/Spinner/ComponentLoader";

// hooks
import { useRbacPrimaryRolePermissions } from "../../../api/hooks";

// selectors
import { getUserID } from "../../../selectors/app";

// sub pages
import Alignment from "./SubPages/Alignment";
import EmployeesWithoutGoals from "./SubPages/EmployeesWithoutGoals";
import GoalsList from "./SubPages/GoalsList";
import Overview from "./SubPages/Overview";
// types
import type { GoalsPage } from "./constants";

const CompanyGoalsPage = () => {
	const { page } = useParams<{ page: GoalsPage }>();
	const { width } = useWindowSize();

	const userID = useSelector((state) => getUserID(state));
	const { data: permissions, isLoading: permissionsLoading } = useRbacPrimaryRolePermissions("goal");
	const { config, isLoading: navLoading } = useNavigation();

	const userCanExportGoalToCsv = permissions?.includes("goals:exportGoalCsv") || false;
	const userCanBulkEditGoals = permissions?.includes("goals:bulkEditGoals") || false;

	const containerRef = useRef(null);

	// Build navigation items from navigation config
	const navItems = useMemo(() => {
		if (!config?.visibleNavTags?.goals) return [];

		// Map backend IDs to SideNavigationLink format
		const idToLinkMap: Record<string, SideNavigationLink> = {
			list: {
				label: "List",
				href: "/company/goals/list",
				key: "list",
			},
			dashboard: {
				label: "Overview",
				href: "/company/goals/dashboard",
				key: "dashboard",
				fullPageLoad: true,
			},
			alignment: {
				label: "Alignment",
				href: "/company/goals/alignment",
				key: "alignment",
			},
			"employees-without-goals": {
				label: "Employees Without Goals",
				href: "/company/goals/employees-without-goals",
				key: "employees-without-goals",
			},
		};

		return config.visibleNavTags.goals.map((id: string) => idToLinkMap[id]).filter(Boolean);
	}, [config?.visibleNavTags?.goals]);

	/*
	 * Here we are getting the goals dashboard from a hidden div'd element in the tornado template that is generated server side.
	 * We are then cloning the element and appending it to the containerRef.current element.
	 * We're then hiding the original and using the clones element for display when the user navigates to the dashboard page.
	 */
	useEffect(() => {
		$("#tornado-template-content").show();
		containerRef.current &&
			$("#goals-dashboard").clone().attr("id", "goals-dashboard-copy").appendTo(containerRef.current);
		$("#goals-dashboard-copy").css("visibility", "visible");
		$("#tornado-template-content").hide();
	}, [page, permissionsLoading, navLoading, width]);

	const isLoading = permissionsLoading || navLoading;

	if (isLoading || navItems.length === 0) {
		return <ComponentLoader />;
	}

	const pageName = page || navItems[0]?.key;

	// Check if user can view each page based on navItems
	const canViewPage = (key: string) => navItems.some((item) => item.key === key);

	return (
		<div>
			{pageName === "list" && canViewPage("list") && (
				<GoalsList
					userCanBulkEditGoals={userCanBulkEditGoals}
					userCanExportToCsv={userCanExportGoalToCsv}
					navItems={navItems}
					userID={userID}
				/>
			)}
			{pageName === "dashboard" && canViewPage("dashboard") && (
				<Overview navItems={navItems} containerRef={containerRef} />
			)}
			{pageName === "alignment" && canViewPage("alignment") && <Alignment navItems={navItems} userID={userID} />}
			{pageName === "employees-without-goals" && canViewPage("employees-without-goals") && (
				<EmployeesWithoutGoals navItems={navItems} />
			)}
		</div>
	);
};

export default CompanyGoalsPage;
